import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { formatNumber } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import {
    ArrowShortLeftIcon,
    ArrowShortUpRightIcon,
    MinusIcon,
    PlusIcon,
    RetryIcon
} from "@solomon-labs/design/components/icons";
import { Button, ButtonIcon, Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { TransactionRow } from "@/components/Data/TransactionRow";
import { useAccountTransactionHistory } from "@/hooks/portfolio/useAccountTransactionHistory";

// Use the existing compact transaction-row date format.
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric"
});
const TRANSACTIONS_PER_PAGE = 6;

function formatTokenAmount(amount: BigDecimal): string {
    return formatNumber(amount, { maximumFractionDigits: BigDecimal.DECIMALS });
}

interface AccountTransactionHistoryProps {
    analytics?: ProductJourney;
    /** Wallet whose swaps and distributions to list; empty string (resolved
     *  view's disconnected value) disables the query and renders the
     *  empty state. */
    walletAddress: string;
    balanceAddress?: string;
    /** Returns control to the caller (action card view). */
    onClose: () => void;
}

/** Connected-wallet swaps and yield distributions, rendered newest first. */
export function AccountTransactionHistory({
    analytics: entryAnalytics,

    walletAddress,
    balanceAddress,
    onClose
}: AccountTransactionHistoryProps): React.ReactElement {
    const localAnalytics = useProductJourney(
        "history",
        "rewards_footer",
        walletAddress
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "viewed");
    const [page, setPage] = React.useState(0);
    React.useEffect(() => setPage(0), [walletAddress]);
    const history = useAccountTransactionHistory(walletAddress, balanceAddress);
    const { entries, isLoading, isEmpty, swapsError, distributionsError } =
        history;
    const totalPages = Math.max(
        1,
        Math.ceil(entries.length / TRANSACTIONS_PER_PAGE)
    );
    const visiblePage = Math.min(page, totalPages - 1);
    const visibleEntries = entries.slice(
        visiblePage * TRANSACTIONS_PER_PAGE,
        (visiblePage + 1) * TRANSACTIONS_PER_PAGE
    );
    const showError = swapsError || distributionsError;
    useProductObservation(analytics, "state_viewed", {
        status: isLoading
            ? "loading"
            : showError
              ? "error"
              : isEmpty
                ? "empty"
                : "ready",
        item_count: entries.length
    });
    const errorLabel =
        swapsError && distributionsError
            ? "transactions"
            : swapsError
              ? "swaps"
              : "distributions";

    return (
        <div
            className="flex flex-col gap-3"
            onClickCapture={(event) => {
                if (
                    event.target instanceof Element &&
                    event.target.closest("a[href]")
                )
                    analytics.send("link_clicked", {
                        link_type: "transaction_explorer"
                    });
            }}
        >
            {/* Back + section title — tight inner gap so they read
                as a unit. */}
            <div className="flex flex-col gap-2">
                <Button
                    variant="link"
                    size="sm"
                    className="self-start"
                    onClick={() => {
                        analytics.send("closed", { close_method: "back" });
                        onClose();
                    }}
                >
                    <span className="inline-flex items-center gap-0.5">
                        <ArrowShortLeftIcon className="size-3" />
                        Back
                    </span>
                </Button>
                <Text variant="h1" className="text-foreground-base">
                    Transactions
                </Text>
            </div>

            {/* Line items — same presentational row as USDv
                `TransactionHistory`, sized down to `small` since the
                action panel is width-constrained. Dividers use the
                `line-base` token (stronger than ListItem's default
                `line-neutral`) so the visual separation between
                rows reads at a glance. The explicit `border-t` on
                top + ListItem's `border-b` on the last row close
                the list on both ends. */}
            <div className="flex flex-col">
                <div className="border-t border-line-base" />
                {isLoading && (
                    <div
                        className="flex flex-col gap-2 py-3"
                        aria-label="Loading transactions"
                        aria-busy="true"
                    >
                        {[0, 1, 2].map((row) => (
                            <div
                                key={row}
                                className="h-9 rounded bg-line-neutral animate-pulse"
                            />
                        ))}
                    </div>
                )}
                {showError && (
                    /* The retry sits inline after the copy so the
                       recovery action is where the user's eye already
                       is. Glyph and copy share `foreground-muted` so
                       the pair reads as one unit — the className
                       overrides ButtonIcon's charcoal default while
                       leaving its ember hover intact. */
                    <div role="alert" className="flex items-center gap-2 py-3">
                        <Text variant="small" className="text-foreground-muted">
                            Couldn&apos;t load {errorLabel}. Please try again.
                        </Text>
                        <ButtonIcon
                            variant="transparent"
                            size="lg"
                            className="text-foreground-muted"
                            icon={<RetryIcon />}
                            aria-label={`Retry loading ${errorLabel}`}
                            onClick={() => {
                                analytics.send("retry_clicked");
                                void history.retry();
                            }}
                        />
                    </div>
                )}
                {isEmpty && (
                    <Text
                        variant="small"
                        className="text-foreground-muted py-3"
                    >
                        No transactions yet.
                    </Text>
                )}
                {!isLoading &&
                    entries.length === 0 &&
                    history.hasMore &&
                    !showError && (
                        <Text
                            variant="small"
                            className="text-foreground-muted py-3"
                        >
                            No transactions in this period. Load older
                            transactions to continue.
                        </Text>
                    )}
                {!isLoading &&
                    visibleEntries.map((entry) => {
                        const distribution = entry.kind === "distribution";
                        const selling = entry.kind === "sell";
                        const { signature } = entry;
                        const date =
                            entry.timestamp > 0
                                ? DATE_FORMAT.format(
                                      new Date(entry.timestamp * 1000)
                                  )
                                : "Unknown date";
                        return (
                            <TransactionRow
                                key={entry.id}
                                itemKey={entry.id}
                                href={
                                    signature
                                        ? `https://solscan.io/tx/${signature}`
                                        : undefined
                                }
                                icon={
                                    selling ? (
                                        <MinusIcon className="size-4 text-foreground-base" />
                                    ) : (
                                        <PlusIcon className="size-4 text-foreground-base" />
                                    )
                                }
                                iconBare
                                trailingIcon={
                                    signature ? (
                                        <ArrowShortUpRightIcon />
                                    ) : undefined
                                }
                                label={
                                    distribution
                                        ? "Yield distribution"
                                        : selling
                                          ? "Sell USDv"
                                          : "Buy USDv"
                                }
                                labelShort={
                                    distribution
                                        ? "Yield"
                                        : selling
                                          ? "Sell"
                                          : "Buy"
                                }
                                primary={`${selling ? "−" : "+"}${formatTokenAmount(entry.amount)} USDv`}
                                secondary={
                                    entry.balance !== undefined
                                        ? `on ${formatTokenAmount(entry.balance)} USDv`
                                        : entry.otherToken
                                          ? `${selling ? "+" : "−"}${formatTokenAmount(entry.otherToken.amount)} ${entry.otherToken.symbol}`
                                          : undefined
                                }
                                date={date}
                                dateShort={date}
                                dateLeading
                                size="sm"
                                borderColor="rgb(var(--color-line-base))"
                            />
                        );
                    })}
                {totalPages > 1 && (
                    <nav
                        aria-label="Transaction pages"
                        className="flex items-center justify-between gap-3 py-3"
                    >
                        <Button
                            variant="link"
                            size="sm"
                            disabled={visiblePage === 0}
                            aria-label="Previous page"
                            onClick={() => {
                                analytics.send("page_selected", {
                                    direction: "previous",
                                    page: visiblePage - 1 + 1
                                });
                                setPage(visiblePage - 1);
                            }}
                        >
                            Previous
                        </Button>
                        <Text variant="small" className="text-foreground-muted">
                            Page {visiblePage + 1} of {totalPages}
                        </Text>
                        <Button
                            variant="link"
                            size="sm"
                            disabled={visiblePage === totalPages - 1}
                            aria-label="Next page"
                            onClick={() => {
                                analytics.send("page_selected", {
                                    direction: "next",
                                    page: visiblePage + 1 + 1
                                });
                                setPage(visiblePage + 1);
                            }}
                        >
                            Next
                        </Button>
                    </nav>
                )}
                {history.hasMore &&
                    !showError &&
                    visiblePage === totalPages - 1 && (
                        <Button
                            variant="link"
                            size="sm"
                            className="self-start py-3"
                            disabled={history.isFetching}
                            onClick={() => {
                                analytics.send("page_selected", {
                                    direction: "more"
                                });
                                void history.loadMore();
                            }}
                        >
                            {history.isFetching
                                ? "Loading transactions…"
                                : "Load older transactions"}
                        </Button>
                    )}
            </div>
        </div>
    );
}
